'use client';

import customFetch from '@/api/fetch';
import { useTranslation } from '@/app/i18n/client';
import { useToast } from '@/components/ui/use-toast';
import { useAuthV2 } from '@/hooks/useAuthV2';
import { getLocalStorage, setLocalStorage } from '@/utils/local-storage';
import { Location, LocationType, UserGroups } from '@/utils/types';
import { useParams, usePathname } from 'next/navigation';
import { Context, createContext, useContext, useEffect, useState } from 'react';

interface LocationContextType {
  locations: Location[];
  setLocations: (locations: Location[]) => void;
  isLoading: boolean;
  handleFetchAllLocations: VoidFunction;
  isOpenModalSetLocation: boolean;
  setIsOpenModalSetLocation: (value: boolean) => void;
  locationModal: LocationType;
  setLocationModal: (value: LocationType) => void;
  handleSetLocation: (location: LocationType) => Promise<any>;
}

export const LocationContext: Context<LocationContextType> = createContext({} as any);

export function LocationProvider({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const { lng } = useParams() as { lng: string };
  const { t } = useTranslation(lng, ['errors']);
  const { userGroups, user } = useAuthV2();
  const pathname = usePathname();

  const isAdmin = userGroups.includes(UserGroups.ADMIN) || userGroups.includes(UserGroups.STAFF);

  const [data, setData] = useState<{
    locations: Location[];
    isLoading: boolean;
    isOpenModalSetLocation: boolean;
    locationModal: LocationType;
  }>({
    locations: [],
    isLoading: false,
    isOpenModalSetLocation: false,
    locationModal: '' as LocationType,
  });

  const setIsOpenModalSetLocation = (status: boolean) => {
    setData((prev) => ({
      ...prev,
      isOpenModalSetLocation: status,
    }));
  };

  const setLocationModal = (id: LocationType) => {
    setData((prev) => ({
      ...prev,
      locationModal: id,
    }));
  };

  const setLocations = (locations: Location[]) => {
    setData((prev) => ({
      ...prev,
      locations,
    }));
  };

  const setIsLoading = (isLoading: boolean) => {
    setData((prev) => ({
      ...prev,
      isLoading,
    }));
  };

  const { isOpenModalSetLocation, locationModal, isLoading, locations } = data;

  const handleFetchAllLocations = async () => {
    setIsLoading(true);
    try {
      const response = await customFetch(`/locations`);
      setLocations(response.locations);
    } catch (error: any) {
      showToast({
        variant: 'error',
        title: t('errors:fetchAllLocations'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetLocation = async (location: LocationType) => {
    try {
      const response = await customFetch(`/users/${user?.id}/location/${location}`, {
        method: 'POST',
      });

      setLocalStorage('location', response.userADMLocation.Location.name);
      setLocationModal(response.userADMLocation.Location.name);
      setIsOpenModalSetLocation(false);

      return response.userADMLocation;
    } catch (error: any) {
      showToast({
        variant: 'error',
        title: error?.code,
        description: error?.message,
      });
    }
  };

  const GetUserLocationHistoric = async () => {
    try {
      const response = await customFetch(`/users/${user?.id}/location`);

      return response.userADMLocations;
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;

    const location = getLocalStorage('location');
    const isValidLocation = !!location && Object.values(LocationType).includes(location as LocationType);

    if (!isValidLocation) {
      GetUserLocationHistoric()
        .then((locations) => {
          const location = locations[0]?.Location?.name;
          if (location) {
            setLocalStorage('location', location);
            setLocationModal(location as LocationType);
            return;
          }
          setIsOpenModalSetLocation(true);
          setLocationModal('' as LocationType);
        })
        .catch((error) => {
          console.error(error);
        });
      return;
    }
    setLocationModal(location as LocationType);
  }, [isAdmin, pathname]);

  // useEffect(() => {
  //   if (isAdmin && !locationModal) {
  //     setIsOpenModalSetLocation(true);
  //   }
  // }, [isAdmin, locationModal]);

  return (
    <LocationContext.Provider
      value={{
        locations,
        setLocations,
        isLoading,
        handleFetchAllLocations,
        isOpenModalSetLocation,
        locationModal,
        setIsOpenModalSetLocation,
        setLocationModal,
        handleSetLocation,
      }}>
      {children}
    </LocationContext.Provider>
  );
}

export default function useLocation() {
  const context = useContext(LocationContext);
  return context;
}
